import { Component, EventEmitter, Input, OnInit, Output, inject, model, signal } from '@angular/core';
import { FormArray, FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { CommonModule, formatDate } from '@angular/common';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatIcon } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { Router } from '@angular/router';
import { MaintenanceBranchService } from './maintenance-branch-service/maintenance-branch-service';
import { HttpStatusCode } from 'axios';
import { branchName, getDirectors, savebranchDetails, viewbranchDetails, saveBanding } from './maintenance-branch-interface/maintenance-branch-interface';
import { MatDatepicker, MatDatepickerModule, MatDatepickerToggle, MatDatepickerToggleIcon } from '@angular/material/datepicker';
import { SnackbarService } from '../../../../shared/services/snackbar/snackbar.service';
import { SessionStorageService } from '../../../../core/services/session-storage/session-storage.service';
import { BranchNamemodel } from './maintenance-branch-model/maintenance-branch-model';
import { MatDialog } from '@angular/material/dialog';
import { NewRepApplicationService } from '../../../agent-onboarding/new-rep-application/new-rep-application-services/new-rep-application.service';
import { viewAttachements } from '../../../agent-onboarding/new-rep-application/new-rep-application-interfaces/attachements.interface';
import { ConfirmPopupComponent } from '../../../agent-onboarding/new-rep-application/confirm-popup/confirm-popup.component';
import { provideMomentDateAdapter } from '@angular/material-moment-adapter';
import { SelectOptions } from '../../../../core/interfaces/select-options.interface';

/** DD/MM/YYYY datepicker convention shared with the onboarding forms. */
export const MY_FORMATS = {
  parse: {
    dateInput: 'DD/MM/YYYY',
  },
  display: {
    dateInput: 'DD/MM/YYYY',
    monthYearLabel: 'MMM YYYY',
    dateA11yLabel: 'DD/MM/YYYY',
    monthYearA11yLabel: 'MMMM YYYY',
  },
};
/**
 * Maintenance-only Branch Management editor: branch info with designation
 * dropdowns, a "Change in Banding" section, and document uploads. All fields
 * are editable (unlike the shared onboarding branch view, which is read-only).
 */
@Component({
  selector: 'app-maintenance-branch',
  providers: [provideMomentDateAdapter(MY_FORMATS)],
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    MatIcon,
    MatButtonModule,
    MatDatepickerModule,
  ],
  templateUrl: './maintenance-branch.component.html',
  styleUrl: './maintenance-branch.component.scss',
})
export class MaintenanceBranchComponent implements OnInit {
  @Input() formGroup!: FormGroup;
  @Input() readonly = signal(false);
  @Output() save = new EventEmitter<string>();
  @Output() next = new EventEmitter<string[]>();
  @Output() preview = new EventEmitter<string[]>();
  private fb = inject(FormBuilder);
  private router = inject(Router);
  private matDialog = inject(MatDialog);
  private maintenancebranchService = inject(MaintenanceBranchService);
  private documentUploadService = inject(NewRepApplicationService);
  private snackBarService = inject(SnackbarService);
  private sessionStorageService = inject(SessionStorageService);
  firstset = signal(false);
  setSprinner = signal(false);
  viewBranchDetails!: viewbranchDetails;
  viewattachments!: viewAttachements[];
  viewnewBranchDetails!: viewbranchDetails[];
  savebanding!: saveBanding[];
  readonly message = "This field is required";

  role = ' ';
  isvisible = signal(false);
  status = 'Not Started';

  /**
   * Branch options for the Branch Name dropdown. Hardcoded for now — same names
   * the maintenance dashboard lists; move to a branch master API when available.
   */
  branches!: branchName[];
  director: getDirectors[][] = [];
  managers: getDirectors[][] = [];

  /** Designation / supervisory-structure options for the FSD & FSM dropdowns. */
  readonly designations: string[] = [
    'Wealth Management Consultant',
    'Senior Wealth Management Consultant',
    'Associate Director (Advisory)',
    'Associate Financial Services Manager',
    'Financial Services Manager',
    'Senior Financial Services Manager',
    'Associate Financial Services Director',
    'Financial Services Director',
    'Senior Financial Services Director',
  ];
  data = history.state;

  protected tierList: SelectOptions[] = [
    {
      value: 'T3',
      name: 'T3'
    },
    {
      value: 'T2',
      name: 'T2'
    },
    {
      value: 'T1',
      name: 'T1'
    },
  ];

  async ngOnInit() {
    //  Use dynamic controlName (reusable fix)
    this.formGroup.addControl(
      'newbranch',
      this.fb.array([])
    );


    //  Always show at least one row
    if (this.formArray.length === 0) {
      this.addRow();
    }

    if (!this.formGroup.contains('banding')) {
      this.formGroup.addControl(
        'banding',
        this.fb.control('', Validators.required)
      );
    }


    this.role = this.sessionStorageService.getCurrentUserRole();
    if (this.role === 'DSU' || this.role === 'DSU Manager') {
      this.isvisible.set(true);
    }

    await this.getBranchNames();
    await this.getviewbranchDetails();


  }

  isLastBranch(index: number): boolean {
    return index === this.formArray.length - 1;
  }

  private applyBranchEditability(): void {
    if (this.readonly()) {
      this.formGroup.disable({ emitEvent: false });
      return;
    }

    // Enable the form first
    this.formGroup.enable({ emitEvent: false });

    // Disable all branches except the last one
    this.formArray.controls.forEach((control, index) => {
      if (index === this.formArray.length - 1) {
        control.enable({ emitEvent: false });
      } else {
        control.disable({ emitEvent: false });
      }
    });

    // Keep Banding editable
    this.formGroup.get('banding')?.enable({ emitEvent: false });
  }

  //  Generic FormArray getter
  get formArray(): FormArray {
    return this.formGroup.get('newbranch') as FormArray;
  }

  public createRow(): FormGroup {
    return this.fb.group({
      branchName: ['', [Validators.required]],
      designation: ['', [Validators.required]],
      fsd: ['', [Validators.required]],
      fsm: [''],
      tier: ['', [Validators.required]],
      remarks: [''],
      type_of_change: ['', [Validators.required]],
      effectiveDate: ['', [Validators.required]],
      branchDocument: [''],
      designationDocument: [''],
      model: ['', [Validators.required]],
      directorRepCode: [''],
      managerRepCode: ['']
    });
  }

  addRow() {
    this.formArray.push(this.createRow());
    this.applyBranchEditability();
  }

  onFileSelected(event: Event, controlName: string, index: number): void {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];
    const control = this.formArray.at(index).get(controlName);

    // ✅ Valid file → update form properly
    this.formArray.at(index).patchValue({
      [controlName]: file?.name
    });

    if (!control) return;

    // Reset first
    control.markAsTouched();
    control.setErrors(null);

    if (!file) {
      control.setValue(null);
      return;
    }

    const allowedTypes = new Set([
      'application/pdf',
      'image/jpeg',
      'image/png'
    ]);

    const maxSize = 5 * 1024 * 1024; // 5MB

    // Validate type
    if (!allowedTypes.has(file.type)) {
      control.setErrors({ invalidType: true });
      return;
    }

    // Validate size
    if (file.size > maxSize) {
      control.setErrors({ maxSize: true });
      return;
    }

    control.updateValueAndValidity();
    this.uploaddocument(file, controlName, index)
  }

  fileName(controlName: string, index: number): string {
    const file = this.formArray.at(index).get(controlName)?.value;
    return file;
  }

  async uploaddocument(file: File, controlName: string, index: number) {

    const formData = new FormData();

    formData.append('file', file);
    formData.append('Description', controlName);
    formData.append('filename', file?.name);
    formData.append('ResponseValue', index.toString());
    formData.append('UserId', String(this.data.applicant.onboardingid));

    const responseData = await this.documentUploadService
      .uploadDocuments(formData)
      .then((response) => {
        return JSON.parse(response);
      })
      .catch((error) => {
        // console.error(error);
        return error;
      });
    if (responseData.status === HttpStatusCode.Ok) {
      this.formGroup.patchValue({
        [controlName]: responseData.data.responsedata
      });
      this.viewDocumants();

      this.snackBarService.openSuccessSnackBar("document uploaded successfully!");
    } else {
      if (responseData.code === HttpStatusCode.BadRequest) {
        // Display notification on API failure.
        this.snackBarService.openFailureSnackBar("We couldn’t process your request. Please try again");
      }
    }
  }


  //  Remove row (keep at least one row)
  async deleteRow(index: number): Promise<void> {
    const dialogRef = this.matDialog.open(ConfirmPopupComponent, {
      width: '500px',
      disableClose: true,
      data: {
        message: 'Do you want to delete the Branch?',
        hidecancel: false,
      }
    });

    dialogRef.afterClosed().subscribe(async result => {
      if (result.data === true) {
        if (this.formArray.length > 1) {
          if (this.viewnewBranchDetails[index]?.id != undefined) {
            let id = this.viewnewBranchDetails[index]?.id;

            const responseData = await this.maintenancebranchService
              .deletebranchRow(id)
              .then((response) => {
                return JSON.parse(response);
              })
              .catch((error) => {
                // console.error(error);
                return error;
              });
          }
          this.formArray.removeAt(index);
          this.applyBranchEditability();
        } else {
          // optional: reset instead of delete last row
          this.formArray.at(0).reset();
        }
      } else {
        // console.log('User cancelled');
      }
    });
  }

  async viewDocumants() {
    let repCode = this.data.applicant.repcode;
    const responseData = await this.maintenancebranchService
      .viewbranchDetails(repCode)
      .then((response) => {
        return JSON.parse(response);
      })
      .catch((error) => {
        // console.error(error);
        return error;
      });

    this.viewattachments = responseData.data.responsedata.documents;
  }

  removeFile(controlName: string, index: number) {
    const dialogRef = this.matDialog.open(ConfirmPopupComponent, {
      width: '500px',
      disableClose: true,
      data: {
        message: 'Do you want to remove the attachement?',
        hidecancel: false,
      }
    });
    this.viewDocumants();
    dialogRef.afterClosed().subscribe(async result => {
      if (result.data === true) {
        if (this.viewattachments) {
          let id;
          let docTypeId;
          this.viewattachments.forEach(item => {
            if (item?.onboardingDocumentType === controlName && index === Number(item?.responseValue)) {
              docTypeId = item.docTypeId;
              id = item.id
            }
          })

          const data = {
            onboardingId: this.data.applicant.onboardingid,
            docTypeId: docTypeId,
            id: id
          }
          const responseData = await this.documentUploadService
            .deletedocument(data)
            .then((response) => {
              return JSON.parse(response);
            })
            .catch((error) => {
              // console.error(error);
              return error;
            });
        }
        this.formArray.at(index).get(controlName)?.reset();
        const fileInput = document.getElementById(controlName) as HTMLInputElement;
        if (fileInput) {
          fileInput.value = '';
        }
        this.formArray.at(index).get(controlName)?.markAsTouched();
        this.formArray.at(index).get(controlName)?.updateValueAndValidity();
      } else {
        // console.log('User cancelled');
      }
    });
  }

  async getBranchNames() {
    const responseData = await this.maintenancebranchService
      .getBranchdetails()
      .then((response) => {
        return JSON.parse(response);
      })
      .catch((error) => {
        // console.error(error);
        return error;
      });
    if (responseData.status === HttpStatusCode.Ok) {
      this.branches = responseData.data.responsedata;
    }
  }

  async getviewbranchDetails() {
    let repCode = this.data.applicant.repcode;
    const responseData = await this.maintenancebranchService
      .viewbranchDetails(repCode)
      .then((response) => {
        return JSON.parse(response);
      })
      .catch((error) => {
        // console.error(error);
        return error;
      });
    this.setSprinner.set(false);
    if (responseData.status === HttpStatusCode.Ok) {
      this.viewattachments = responseData.data.responsedata.documents;
      this.viewnewBranchDetails = responseData.data.responsedata.new_branch;
      if (this.viewnewBranchDetails.length > 0) {
        this.formArray.clear();
      }
      for (let index = 0; index < this.viewnewBranchDetails.length; index++) {
        const item = this.viewnewBranchDetails[index];
        this.addRow();

        // this.getdirectors(index);
        this.formArray.at(index).patchValue({
          branchName: item.branch,
          designation: item.designation,
          fsd: item.financialServicesDirector,
          fsm: item.financialServicesManager,
          effectiveDate: item.effectiveDate,
          tier: item.tier,
          remarks: item.remarks,
          type_of_change: item.typeOfChange,
          model: item.model

        });

        this.formGroup.patchValue({
          banding: item.representativeBanding
        });

        await this.getdirectors(index);


        const row = this.formArray.at(index) as FormGroup;

        const directorName = String(row.get('fsd')?.value ?? '').trim();
        const managerName = String(row.get('fsm')?.value ?? '').trim();

        const selectedDirector = this.director[index]?.find(
          item =>
            item.userRole === 'Director' &&
            String(item.dir ?? '').trim() === directorName
        );

        const selectedManager = this.managers[index]?.find(
          item =>
            item.userRole === 'Manager' &&
            String(item.mgr ?? '').trim() === managerName
        );

        row.patchValue({
          directorRepCode: selectedDirector?.dirRepCode ?? '',
          managerRepCode: selectedManager?.manRepCode ?? ''
        });

        this.viewattachments.forEach(item => {
          if (item?.onboardingDocumentType === 'branchDocument' && index === Number(item?.responseValue)) {
            this.formArray.at(index).patchValue({
              branchDocument: item.fileName

            })
          } else if (item?.onboardingDocumentType === 'designationDocument' && index === Number(item?.responseValue)) {
            this.formArray.at(index).patchValue({
              designationDocument: item.fileName
            })
          }
        });
      };
      this.applyBranchEditability();
    }
  }

  previewAttachment(controlName: string, index: number) {
    if (this.viewattachments) {
      let id;
      this.viewattachments.forEach(item => {
        if (item?.onboardingDocumentType === controlName && index === Number(item?.responseValue)) {
          id = item.id;
        }
      });
      let filename = this.formArray.at(index).get(controlName)?.value;
      this.preview.emit([id, filename]);
    }
  }

  async saveSection() {
    let savebranchmanagement = new BranchNamemodel();
    const formArray = this.formGroup.get('newbranch') as FormArray;

    if (formArray.invalid) {
      formArray.markAllAsTouched();
      this.snackBarService.openWarningSnackBar(
        'Please resolve the highlighted fields before saving.'
      );
      return;
    }

    const branchDetails = formArray.controls.map(
      (control: any, index: number) => ({
        id: this.viewnewBranchDetails[index]?.id == 0 ? 0 : this.viewnewBranchDetails[index]?.id,
        pias_rep_code: this.data.applicant.repcode,
        branch: control.get('branchName')?.value,
        effective_date: control.get('effectiveDate')?.value ? formatDate(control.get('effectiveDate')?.value, 'yyyy-MM-dd', 'en-US') : '',
        financial_service_manager: control.get('fsm')?.value,
        group_director: control.get('fsd')?.value,
        designation: control.get('designation')?.value,
        type_of_change: control.get('type_of_change')?.value,
        tier: control.get('tier')?.value,
        remarks: control.get('remarks')?.value,
        model: control.get('model')?.value,
        directorRepCode: control.get('directorRepCode')?.value,
        managerRepCode: control.get('managerRepCode')?.value,

      }));
    savebranchmanagement.setsavebranch([...branchDetails]);

    const repCode = this.data.applicant.repcode;
    const banding = this.formGroup.get('banding')?.value;

    try {
      const [branchResponse, bandingResponse] = await Promise.all([
        this.maintenancebranchService.savebranchDetails(savebranchmanagement),
        this.maintenancebranchService.saveBanding(repCode, banding)
      ]);

      const responseData = JSON.parse(branchResponse);
      const bandingResponseData = JSON.parse(bandingResponse);

      if (
        responseData.status === HttpStatusCode.Ok &&
        bandingResponseData.status === HttpStatusCode.Ok
      ) {
        this.save.emit('saved');
      } else {
        this.snackBarService.openFailureSnackBar(
          'We couldn’t process your request. Please try again'
        );
      }
    } catch (error) {
      this.snackBarService.openFailureSnackBar(
        'We couldn’t process your request. Please try again'
      );
    } finally {
      this.setSprinner.set(false);
    }
  }
  cancelSection() {
    this.save.emit('cancelled');
  }

  async getdirectors(index: number) {
    const row = this.formArray.at(index) as FormGroup;
    const branchName = row.get('branchName')?.value;
    const branch = this.branches?.find(item => item.branchname === branchName);
    const branchid = Number(branch?.branchid);
    const responseData = await this.maintenancebranchService
      .getdirector(branchid)
      .then((response) => {
        return JSON.parse(response);
      })
      .catch((error) => {
        // console.error(error);
        return error;
      });
    this.setSprinner.set(false);
    if (responseData.status === HttpStatusCode.Ok) {
      this.director[index] = responseData.data.responsedata.filter((item: any) => item.userRole === 'Director');
      this.managers[index] = responseData.data.responsedata.filter((item: any) => item.userRole === 'Manager');
      // row.get('fsd')?.setValue(this.director[0]?.dir);
      // row.get('fsm')?.setValue(this.managers[0]?.mgr);
    } else {
      if (responseData.code === HttpStatusCode.BadRequest) {
        // Display notification on API failure.
        this.snackBarService.openFailureSnackBar(
          'We couldn’t process your request. Please try again',
        );
      }
      if (responseData.code === HttpStatusCode.Unauthorized) {
        this.snackBarService.openFailureSnackBar('Session timed out. Try logging in again!');
        this.router.navigate(['/login']);
      }
    }
  }

  async getdirectorsbyindex(index: number) {
    const row = this.formArray.at(index) as FormGroup;
    const branchName = row.get('branchName')?.value;

    const branch = this.branches?.find(
      item => item.branchname === branchName
    );

    const branchid = Number(branch?.branchid);

    if (!branchid) {
      row.patchValue({
        fsd: '',
        directorRepCode: '',
        fsm: '',
        managerRepCode: ''
      });

      this.director[index] = [];
      this.managers[index] = [];
      return;
    }

    const responseData = await this.maintenancebranchService
      .getdirector(branchid)
      .then(response => JSON.parse(response))
      .catch(error => error);

    this.setSprinner.set(false);

    if (responseData.status === HttpStatusCode.Ok) {
      this.director[index] =
        responseData.data.responsedata.filter(
          (item: any) => item.userRole === 'Director'
        );

      this.managers[index] =
        responseData.data.responsedata.filter(
          (item: any) => item.userRole === 'Manager'
        );

      // Get the first director after the API response is loaded.
      const selectedDirector = this.director[index][0];

      // Set the director name and rep code.
      row.patchValue({
        fsd: selectedDirector?.dir ?? '',
        directorRepCode: selectedDirector?.dirRepCode ?? '',
        fsm: '',
        managerRepCode: ''
      });

    } else {
      if (responseData.code === HttpStatusCode.BadRequest) {
        this.snackBarService.openFailureSnackBar(
          'We couldn’t process your request. Please try again'
        );
      }

      if (responseData.code === HttpStatusCode.Unauthorized) {
        this.snackBarService.openFailureSnackBar(
          'Session timed out. Try logging in again!'
        );
        this.router.navigate(['/login']);
      }
    }
  }

  onDirectorChange(selectedDirector: string, index: number): void {
    const selected = this.director[index]?.find(
      (item: any) => item.dir === selectedDirector
    );

    this.formArray.at(index).get('directorRepCode')?.setValue(
      selected?.dirRepCode ?? ''
    );
  }

  onManagerChange(selectedManager: string, index: number): void {
    const selected = this.managers[index]?.find(
      (item: any) => item.mgr === selectedManager
    );

    this.formArray.at(index).get('managerRepCode')?.setValue(
      selected?.manRepCode ?? ''
    );
  }

}
